import type { SpotifyEpisode } from '@/utilities/spotify'
import { formatDuration } from '@/utilities/spotify'
import { ArrowRightIcon, Calendar, Clock, Headphones } from 'lucide-react'

type Props = {
  episode: SpotifyEpisode
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const PodcastCard = ({ episode }: Props) => {
  const imageURL = episode.images?.[0]?.url
  const formattedDate = formatDate(episode.release_date)
  const duration = formatDuration(episode.duration_ms)

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/50 bg-card shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5">
      {/* Image Section - bigger than blog cards */}
      {imageURL && (
        <a
          href={episode.external_urls.spotify}
          target="_blank"
          rel="noopener noreferrer"
          className="relative aspect-square overflow-hidden"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={episode.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            src={imageURL}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          {/* Play badge */}
          <div className="absolute bottom-4 right-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#1DB954] text-white shadow-lg opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-110">
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 ml-0.5">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>

          {/* Podcast badge */}
          <div className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-[#1DB954]/90 px-3 py-1 text-xs font-semibold text-white shadow-lg backdrop-blur-sm">
            <Headphones className="h-3 w-3" />
            Podcast
          </div>
        </a>
      )}

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        {/* Meta info */}
        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>{duration}</span>
          </div>
        </div>

        {/* Title */}
        <a
          href={episode.external_urls.spotify}
          target="_blank"
          rel="noopener noreferrer"
          className="group/title mb-3"
        >
          <h3 className="text-xl font-bold leading-snug text-foreground transition-colors group-hover/title:text-primary line-clamp-2">
            {episode.name}
          </h3>
        </a>

        {/* Description */}
        <p className="mb-4 line-clamp-2 flex-1 text-sm leading-relaxed text-muted-foreground">
          {episode.description}
        </p>

        {/* CTA */}
        <div className="mt-auto">
          <a
            href={episode.external_urls.spotify}
            target="_blank"
            rel="noopener noreferrer"
            className="group/btn inline-flex items-center gap-2 rounded-full bg-[#1DB954]/15 px-5 py-2.5 text-sm font-semibold text-[#0F5B2E] transition-all hover:bg-[#1DB954] hover:text-white"
          >
            <span>Beluister</span>
            <ArrowRightIcon
              aria-hidden
              className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5"
            />
          </a>
        </div>
      </div>
    </article>
  )
}
